import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFeedback } from '../../hooks/useMarketing';

const DATE_PRESETS = [
  { label: '7 days', value: '7' },
  { label: '30 days', value: '30' },
  { label: '90 days', value: '90' },
  { label: 'All time', value: '' },
];

const REASON_META = {
  price: { label: 'Price', icon: '💰', color: '#d97706' },
  not_ready: { label: 'Not ready', icon: '⏳', color: '#3b82f6' },
  comparing: { label: 'Comparing', icon: '🔍', color: '#8b5cf6' },
  question: { label: 'Question', icon: '❓', color: '#0f766e' },
  just_wanted_report: { label: 'Just wanted report', icon: '📄', color: '#0891b2' },
  report_concerns: { label: 'Report concerns', icon: '⚠️', color: '#dc2626' },
  other: { label: 'Other', icon: '💬', color: '#64748b' },
  comment_only: { label: 'Comment only', icon: '📝', color: '#64748b' },
};

const SOURCE_LABELS = {
  checkout_prompt: 'Checkout prompt',
  email: 'Email link',
};

const TEMPLATE_LABELS = {
  wizard_follow_up: '👋 First Contact',
  wizard_report_follow_up: '📊 Analysis Report',
  wizard_checkout_follow_up: '🛒 Checkout Nudge',
  wizard_second_follow_up: '⏰ Final Note',
  wizard_feedback: '💬 Feedback Ask',
  quote_report: '📄 Report & Quote',
  quote_follow_up: '💳 Quote Nudge',
  quote_revised: '🧾 Updated Quote',
};

function reasonMeta(reason) {
  return REASON_META[reason || 'comment_only'] || REASON_META.other;
}

function timeAgo(iso) {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${Math.max(mins, 1)}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(iso).toLocaleDateString();
}

function Feedback() {
  const navigate = useNavigate();
  const [days, setDays] = useState('30');
  const [reasonFilter, setReasonFilter] = useState(null);
  const [tab, setTab] = useState('feedback');
  // null | 'opened' | 'clicked' — set by clicking the summary tiles
  const [emailFilter, setEmailFilter] = useState(null);
  const { data, isLoading, error, refetch } = useFeedback(days ? { days } : {});

  if (isLoading) {
    return (
      <div className="mkt-loading">
        <div className="mkt-loading-spinner" />
        <p>Loading feedback...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mkt-funnel-error">
        <p>Failed to load feedback: {error.message}</p>
        <button onClick={() => refetch()} className="mkt-btn mkt-btn-primary">Retry</button>
      </div>
    );
  }

  const { entries = [], summary = {}, emails = [], emailSummary = {} } = data || {};
  const filtered = reasonFilter
    ? entries.filter((e) => (e.reason || 'comment_only') === reasonFilter)
    : entries;

  return (
    <div className="mkt-feedback">
      {/* Header */}
      <div className="mkt-funnel-header">
        <h2>Visitor Feedback</h2>
        <div className="mkt-funnel-filters">
          {DATE_PRESETS.map((p) => (
            <button
              key={p.value}
              className={`mkt-filter-btn ${days === p.value ? 'active' : ''}`}
              onClick={() => setDays(p.value)}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {/* Tabs: what they told us / whether they read our emails */}
      <div className="mkt-feedback-tabs">
        <button
          className={`mkt-filter-btn ${tab === 'feedback' ? 'active' : ''}`}
          onClick={() => setTab('feedback')}
        >
          💬 Feedback ({summary.total || 0})
        </button>
        <button
          className={`mkt-filter-btn ${tab === 'emails' ? 'active' : ''}`}
          onClick={() => setTab('emails')}
        >
          ✉️ Email opens ({emailSummary.opened || 0}/{emailSummary.trackableSent ?? emailSummary.sent ?? 0} tracked)
        </button>
      </div>

      {tab === 'emails' ? (
        <>
          <div className="mkt-funnel-summary">
            <div
              className={`mkt-summary-card mkt-feedback-card ${emailFilter === null ? 'active' : ''}`}
              onClick={() => setEmailFilter(null)}
            >
              <span className="mkt-summary-label">✉️ Emails sent</span>
              <span className="mkt-summary-value">{emailSummary.sent || 0}</span>
              <span className="mkt-summary-rate">
                {emailSummary.recipients || 0} people · {emailSummary.trackableSent || 0} with open tracking
              </span>
            </div>
            <div
              className={`mkt-summary-card mkt-feedback-card ${emailFilter === 'opened' ? 'active' : ''}`}
              onClick={() => setEmailFilter(emailFilter === 'opened' ? null : 'opened')}
              title="Click to see who opened"
            >
              <span className="mkt-summary-label">👀 Opened</span>
              <span className="mkt-summary-value">{emailSummary.opened || 0}</span>
              <span className="mkt-summary-rate">
                {emailSummary.openRate || 0}% of {emailSummary.trackableSent || 0} tracked
              </span>
            </div>
            <div
              className={`mkt-summary-card mkt-feedback-card ${emailFilter === 'clicked' ? 'active' : ''}`}
              onClick={() => setEmailFilter(emailFilter === 'clicked' ? null : 'clicked')}
              title="Click to see who clicked"
            >
              <span className="mkt-summary-label">🔗 Clicked through</span>
              <span className="mkt-summary-value">{emailSummary.clicked || 0}</span>
            </div>
          </div>
          <p className="mkt-feedback-note">
            Opens come from a tracking pixel and from link clicks (a click always counts as an
            open). Apple Mail pre-loads images (overcounts) and some clients block them
            (undercounts) — treat as a signal, not an exact measure. Emails sent before open
            tracking shipped (21 July) can never show opens; if that visitor clicked a link,
            the row shows &ldquo;No open data&rdquo; instead.
          </p>
          {(() => {
            const shownEmails = emailFilter === 'opened'
              ? emails.filter((m) => m.openedAt)
              : emailFilter === 'clicked'
                ? emails.filter((m) => m.clickedAt || m.visitorClickedAnyLink)
                : emails;
            return shownEmails.length === 0 ? (
            <div className="mkt-feedback-empty">
              <p>
                {emailFilter === 'opened' && 'No opened emails in this period.'}
                {emailFilter === 'clicked' && 'No clicked emails in this period.'}
                {!emailFilter && 'No outreach emails sent in this period.'}
              </p>
            </div>
          ) : (
            <div className="mkt-feedback-list">
              {shownEmails.map((m) => (
                <div
                  key={m.id}
                  className="mkt-feedback-row"
                  onClick={() => m.visitorId && navigate(`/marketing/visitor/${m.visitorId}`)}
                >
                  <div className="mkt-feedback-row-main">
                    <div className="mkt-feedback-row-top">
                      {m.openedAt ? (
                        <span className="mkt-feedback-reason" style={{ background: '#0f766e18', color: '#0f766e' }}>
                          👀 Opened{m.clickedAt && m.openCount === 0 ? ' (via click)' : m.openCount > 1 ? ` ×${m.openCount}` : ''}
                        </span>
                      ) : m.visitorClickedAnyLink ? (
                        <span
                          className="mkt-feedback-reason"
                          style={{ background: '#d9770618', color: '#d97706' }}
                          title="Sent before per-email tracking: this visitor clicked a link in one of their emails, but we can't tell which"
                        >
                          — No open data
                        </span>
                      ) : (
                        <span className="mkt-feedback-reason" style={{ background: '#64748b18', color: '#64748b' }}>
                          — Not opened
                        </span>
                      )}
                      <span className="mkt-feedback-who">{m.name || m.email || 'Unknown'}</span>
                      {m.email && m.name && <span className="mkt-feedback-email">{m.email}</span>}
                      {m.trademarkName && (
                        <span className="mkt-visitor-tm">&ldquo;{m.trademarkName}&rdquo;</span>
                      )}
                      {m.clickedAt ? (
                        <span className="mkt-badge mkt-badge-returning">Clicked this email</span>
                      ) : m.visitorClickedAnyLink ? (
                        <span className="mkt-badge mkt-badge-feedback-asked" title="Clicked a link in one of their emails (sent before per-email attribution)">
                          Clicked an email
                        </span>
                      ) : null}
                      {m.gaveFeedback && <span className="mkt-badge mkt-badge-feedback-given">💬 Gave feedback</span>}
                    </div>
                    <div className="mkt-feedback-row-meta">
                      <span>{TEMPLATE_LABELS[m.template] || m.template || 'email'}</span>
                      <span>· sent {timeAgo(m.sentAt)}</span>
                      {m.openedAt && <span>· opened {timeAgo(m.openedAt)}</span>}
                    </div>
                  </div>
                  <span className="mkt-feedback-row-arrow">→</span>
                </div>
              ))}
            </div>
          );
          })()}
        </>
      ) : (
        <>
      {/* Summary cards — click a reason card to filter the list */}
      <div className="mkt-funnel-summary">
        <div
          className={`mkt-summary-card mkt-feedback-card ${reasonFilter === null ? 'active' : ''}`}
          onClick={() => setReasonFilter(null)}
        >
          <span className="mkt-summary-label">Total</span>
          <span className="mkt-summary-value">{summary.total || 0}</span>
          <span className="mkt-summary-rate">{summary.visitors || 0} visitors</span>
        </div>
        {Object.entries(REASON_META)
          .filter(([key]) => (summary.byReason || {})[key])
          .map(([key, meta]) => (
            <div
              key={key}
              className={`mkt-summary-card mkt-feedback-card ${reasonFilter === key ? 'active' : ''}`}
              onClick={() => setReasonFilter(reasonFilter === key ? null : key)}
            >
              <span className="mkt-summary-label">{meta.icon} {meta.label}</span>
              <span className="mkt-summary-value" style={{ color: meta.color }}>
                {summary.byReason[key]}
              </span>
              <span className="mkt-summary-rate">
                {summary.total ? Math.round((summary.byReason[key] / summary.total) * 100) : 0}%
              </span>
            </div>
          ))}
        <div className="mkt-summary-card">
          <span className="mkt-summary-label">💬 With comments</span>
          <span className="mkt-summary-value">{summary.withComment || 0}</span>
        </div>
      </div>

      {/* Feedback list */}
      {filtered.length === 0 ? (
        <div className="mkt-feedback-empty">
          <p>No feedback {reasonFilter ? 'for this reason ' : ''}in this period.</p>
          <p className="mkt-feedback-empty-hint">
            Feedback arrives from the checkout prompt and the one-tap links in the feedback email.
          </p>
        </div>
      ) : (
        <div className="mkt-feedback-list">
          {filtered.map((e) => {
            const meta = reasonMeta(e.reason);
            return (
              <div
                key={e.id}
                className="mkt-feedback-row"
                onClick={() => e.visitorId && navigate(`/marketing/visitor/${e.visitorId}`)}
              >
                <div className="mkt-feedback-row-main">
                  <div className="mkt-feedback-row-top">
                    <span className="mkt-feedback-reason" style={{ background: `${meta.color}18`, color: meta.color }}>
                      {meta.icon} {meta.label}
                    </span>
                    <span className="mkt-feedback-who">
                      {e.name || e.email || 'Unknown visitor'}
                    </span>
                    {e.email && e.name && (
                      <span className="mkt-feedback-email">{e.email}</span>
                    )}
                    {e.trademarkName && (
                      <span className="mkt-visitor-tm">&ldquo;{e.trademarkName}&rdquo;</span>
                    )}
                    {e.paid && <span className="mkt-badge mkt-badge-returning">Paid anyway</span>}
                  </div>
                  {e.comment && (
                    <p className="mkt-feedback-comment">&ldquo;{e.comment}&rdquo;</p>
                  )}
                  <div className="mkt-feedback-row-meta">
                    <span>{SOURCE_LABELS[e.source] || e.source || 'unknown source'}</span>
                    {e.stage && <span>· at {e.stage}</span>}
                    {e.phone && (
                      <a href={`tel:${e.phone}`} onClick={(ev) => ev.stopPropagation()} style={{ color: '#0f766e' }}>
                        · 📞 {e.phone}
                      </a>
                    )}
                    {e.trigger && <span>· {e.trigger.replace('_', ' ')}</span>}
                    {e.cartValue && <span>· ${Number(e.cartValue).toLocaleString()} cart</span>}
                    {e.trafficSource && <span>· {e.trafficSource}</span>}
                    <span>· {timeAgo(e.createdAt)}</span>
                  </div>
                </div>
                <span className="mkt-feedback-row-arrow">→</span>
              </div>
            );
          })}
        </div>
      )}
        </>
      )}
    </div>
  );
}

export default Feedback;
